import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type Dispatch,
  type SetStateAction,
  type ReactElement,
  type ReactNode,
} from "react";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";
import {
  widgetSpacingKeys,
  widgetSpacingStyles,
  widgetSpacingFromStyles,
  allowedStyleProperties,
  safeStyleSchema,
  stripMarkup,
  wordpressPostSchema,
  youtubeListValueSchema,
  youtubePlaylistId,
  youtubeValueSchema,
  youtubeVideoId,
  type LayoutNode,
  type SlideFieldValues,
  type SlideTypeDefinition,
  type VideoPlaybackState,
} from "@qassist/schemas";
import {
  enableYoutubeSound,
  playerMediaInteractionEvent,
  rotateYoutubeListForInitialSkip,
  startYoutubeShuffle,
  youtubePlaylistStartIndex,
} from "./youtube.js";
import { vCardQrContent } from "./vcard.js";

export { vCardQrContent } from "./vcard.js";
export { playerMediaInteractionEvent, requestPlayerMediaInteraction } from "./youtube.js";

const styleKeys = new Set<string>(allowedStyleProperties);

function toCssProperties(input: Record<string, string | number> | undefined): CSSProperties {
  if (!input) return {};
  const result: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(input)) {
    if (styleKeys.has(key) && safeStyleSchema.safeParse({ [key]: value }).success)
      result[key] = value;
  }
  return result;
}

function centerPositionStyles(node: LayoutNode): CSSProperties {
  if (node.positioning?.mode !== "center") return {};
  const dimension = (
    value: string | number | undefined,
    fallback: string,
  ): { css: string; half: string } => {
    if (typeof value === "number") return { css: `${value}px`, half: `${value / 2}px` };
    if (typeof value === "string") {
      const parsed = Number.parseFloat(value);
      if (Number.isFinite(parsed)) {
        const unit = value.trim().endsWith("px") ? "px" : "%";
        return { css: `${parsed}${unit}`, half: `${parsed / 2}${unit}` };
      }
    }
    const parsedFallback = Number.parseFloat(fallback);
    const fallbackUnit = fallback.endsWith("px") ? "px" : "%";
    return {
      css: fallback,
      half: `${parsedFallback / 2}${fallbackUnit}`,
    };
  };
  const offset = ({ value, unit }: { value: number; unit: "%" | "px" }): string =>
    value < 0 ? `- ${Math.abs(value)}${unit}` : `+ ${value}${unit}`;
  const width = dimension(node.styles?.width, "25%");
  const height = dimension(node.styles?.height, "25%");
  return {
    left: `calc(50% ${offset(node.positioning.horizontalOffset)} - ${width.half})`,
    right: "auto",
    top: `calc(50% ${offset(node.positioning.verticalOffset)} - ${height.half})`,
    bottom: "auto",
    width: width.css,
    height: height.css,
  };
}

function assetUrl(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (
    value &&
    typeof value === "object" &&
    "url" in value &&
    typeof (value as { url?: unknown }).url === "string"
  ) {
    return (value as { url: string }).url;
  }
  return undefined;
}

function fieldText(value: unknown): string {
  if (typeof value === "string") return stripMarkup(value);
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

function fieldHtml(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function tickerItems(value: unknown): string[] {
  if (Array.isArray(value))
    return value.filter((item): item is string => typeof item === "string" && Boolean(item.trim()));
  if (value && typeof value === "object") {
    const ticker = value as { headlines?: unknown };
    if (Array.isArray(ticker.headlines))
      return ticker.headlines.filter(
        (item): item is string => typeof item === "string" && Boolean(item.trim()),
      );
    if (typeof ticker.headlines === "string") return tickerItems(ticker.headlines);
  }
  if (typeof value !== "string") return [];
  return value
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function Ticker({
  className,
  style,
  node,
  items,
}: {
  className: string;
  style: CSSProperties;
  node: LayoutNode;
  items: string[];
}) {
  const sequenceRef = useRef<HTMLSpanElement>(null);
  const ticker = node.ticker ?? {
    direction: "left" as const,
    speed: 80,
    separator: "◆",
    separatorColor: "currentColor",
    separatorSpacing: 24,
    sequenceGap: 80,
  };
  const [durationSeconds, setDurationSeconds] = useState(24);

  useLayoutEffect(() => {
    const sequence = sequenceRef.current;
    if (!sequence) return;
    const updateDuration = (): void => {
      setDurationSeconds(Math.max(1, sequence.scrollWidth / ticker.speed));
    };
    updateDuration();
    window.addEventListener("resize", updateDuration);
    const observer =
      typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(updateDuration);
    observer?.observe(sequence);
    void document.fonts?.ready.then(updateDuration);
    return () => {
      window.removeEventListener("resize", updateDuration);
      observer?.disconnect();
    };
  }, [items, ticker.sequenceGap, ticker.separator, ticker.separatorSpacing, ticker.speed]);

  const sequence = (hidden: boolean, sequenceRefValue?: typeof sequenceRef) => (
    <span
      className="qr-ticker-sequence"
      aria-hidden={hidden ? "true" : undefined}
      ref={sequenceRefValue}
      style={{ paddingInlineEnd: ticker.sequenceGap }}
    >
      {items.map((item, index) => (
        <span className="qr-ticker-item" key={`${item}-${index}`}>
          <span>{item}</span>
          {index < items.length - 1 ? (
            <span
              className="qr-ticker-separator"
              aria-hidden="true"
              style={{
                color: ticker.separatorColor,
                marginInline: ticker.separatorSpacing,
              }}
            >
              {ticker.separator}
            </span>
          ) : null}
        </span>
      ))}
    </span>
  );

  return (
    <div
      className={className}
      style={style}
      role="marquee"
      aria-label="News ticker"
      data-direction={ticker.direction}
      data-speed={ticker.speed}
    >
      <div
        className={`qr-ticker-track qr-ticker-track--${ticker.direction}`}
        style={{ animationDuration: `${durationSeconds}s` }}
      >
        {sequence(false, sequenceRef)}
        {sequence(true)}
        {sequence(true)}
      </div>
    </div>
  );
}

function TextBox({
  className,
  style,
  node,
  text,
}: {
  className: string;
  style: CSSProperties;
  node: LayoutNode;
  text: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [fittedFontSize, setFittedFontSize] = useState<number | undefined>();

  useLayoutEffect(() => {
    if (node.textFit !== "shrink") {
      setFittedFontSize(undefined);
      return;
    }
    const element = ref.current;
    if (!element) return;
    const requested = Number.parseFloat(window.getComputedStyle(element).fontSize) || 16;
    const minimum = Math.min(requested, node.minFontSize ?? 12);
    const fit = () => {
      let low = minimum;
      let high = requested;
      element.style.fontSize = `${requested}px`;
      for (let index = 0; index < 12 && high - low > 0.5; index += 1) {
        const middle = (low + high) / 2;
        element.style.fontSize = `${middle}px`;
        if (
          element.scrollHeight <= element.clientHeight + 1 &&
          element.scrollWidth <= element.clientWidth + 1
        )
          low = middle;
        else high = middle;
      }
      setFittedFontSize(Math.floor(low * 10) / 10);
    };
    fit();
    const ResizeObserverClass = window.ResizeObserver;
    if (ResizeObserverClass) {
      const observer = new ResizeObserverClass(fit);
      observer.observe(element);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [node.minFontSize, node.textFit, node.textWrap, text]);

  return (
    <div
      ref={ref}
      className={className}
      style={{ ...style, ...(fittedFontSize ? { fontSize: fittedFontSize } : {}) }}
    >
      {text}
    </div>
  );
}

type CarouselKind = "imageCarousel" | "textCarousel" | "contentCarousel";

const WordPressPlaybackContext = createContext<{
  indices: Map<string, number>;
  setIndices: Dispatch<SetStateAction<Map<string, number>>>;
} | null>(null);

export function wordpressPostUrl(value: unknown, index: number): string {
  if (!value || typeof value !== "object" || !("posts" in value) || !Array.isArray(value.posts))
    return "";
  const item: unknown =
    value.posts[Math.min(Math.max(0, index), Math.max(0, value.posts.length - 1))];
  if (!item || typeof item !== "object" || !("url" in item)) return "";
  const parsed = wordpressPostSchema.shape.url.safeParse(item.url);
  return parsed.success ? (parsed.data ?? "") : "";
}

function Carousel({
  kind,
  className,
  style,
  node,
  value,
  interactive,
  wordpress = false,
}: {
  kind: CarouselKind;
  className: string;
  style: CSSProperties;
  node: LayoutNode;
  value: unknown;
  interactive: boolean;
  wordpress?: boolean;
}): ReactElement {
  const items = Array.isArray(value) ? value : [];
  const settings = node.carousel!;
  const playback = useContext(WordPressPlaybackContext);
  const setIndices = playback?.setIndices;
  const [localIndex, setLocalIndex] = useState(0);
  const sourceBinding = wordpress ? node.binding : undefined;
  const activeIndex =
    sourceBinding && playback ? (playback.indices.get(sourceBinding) ?? 0) : localIndex;
  const setActiveIndex = useCallback(
    (update: SetStateAction<number>): void => {
      if (!sourceBinding || !setIndices) {
        setLocalIndex(update);
        return;
      }
      setIndices((indices) => {
        const previous = indices.get(sourceBinding) ?? 0;
        const next = typeof update === "function" ? update(previous) : update;
        return next === previous ? indices : new Map(indices).set(sourceBinding, next);
      });
    },
    [setIndices, sourceBinding],
  );
  const [textScale, setTextScale] = useState(1);
  const contentRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActiveIndex((current) => Math.min(current, Math.max(0, items.length - 1)));
  }, [items.length, setActiveIndex]);

  useEffect(() => {
    if (!settings.autoplay || items.length < 2) return;
    if (!settings.loop && activeIndex >= items.length - 1) return;
    const timer = window.setTimeout(
      () =>
        setActiveIndex((current) =>
          current >= items.length - 1 ? (settings.loop ? 0 : current) : current + 1,
        ),
      settings.intervalMs,
    );
    return () => window.clearTimeout(timer);
  }, [
    activeIndex,
    items.length,
    settings.autoplay,
    settings.intervalMs,
    settings.loop,
    setActiveIndex,
  ]);

  const goTo = (next: number): void => {
    if (!items.length) return;
    if (settings.loop) setActiveIndex((next + items.length) % items.length);
    else setActiveIndex(Math.min(items.length - 1, Math.max(0, next)));
  };
  const item = items[activeIndex] as unknown;
  const record = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
  const imageValue = kind === "imageCarousel" ? item : record.image;
  const imageSource = assetUrl(imageValue);
  const title = kind === "imageCarousel" ? "" : fieldText(record.title);
  const description =
    kind === "imageCarousel" || (wordpress && settings.showExcerpt === false)
      ? ""
      : fieldText(record.description);
  useLayoutEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement || (!title && !description)) {
      setTextScale(1);
      return;
    }
    const fit = (): void => {
      contentElement.style.gap = `${settings.textGap}px`;
      if (titleRef.current) titleRef.current.style.fontSize = `${settings.title.fontSize}px`;
      if (descriptionRef.current)
        descriptionRef.current.style.fontSize = `${settings.description.fontSize}px`;
      const widthScale = contentElement.scrollWidth
        ? contentElement.clientWidth / contentElement.scrollWidth
        : 1;
      const heightScale = contentElement.scrollHeight
        ? contentElement.clientHeight / contentElement.scrollHeight
        : 1;
      const next = Math.max(0.125, Math.min(1, widthScale, heightScale));
      setTextScale((current) => (Math.abs(current - next) < 0.005 ? current : next));
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(fit);
    observer.observe(contentElement);
    return () => observer.disconnect();
  }, [
    description,
    settings.description.fontSize,
    settings.textGap,
    settings.title.fontSize,
    title,
  ]);
  const vertical = settings.direction === "up" || settings.direction === "down";
  const previousSymbol =
    settings.direction === "up" ? "⌃" : settings.direction === "down" ? "⌄" : "‹";
  const nextSymbol = settings.direction === "up" ? "⌄" : settings.direction === "down" ? "⌃" : "›";
  const titleStyle: CSSProperties = {
    color: settings.title.color,
    fontFamily: settings.title.fontFamily,
    fontSize: settings.title.fontSize * textScale,
    fontWeight: settings.title.fontWeight,
    fontStyle: settings.title.fontStyle,
    textDecoration: settings.title.textDecoration,
    textTransform: settings.title.textTransform,
    letterSpacing: settings.title.letterSpacing,
    lineHeight: settings.title.lineHeight,
    textAlign: settings.title.textAlign,
  };
  const descriptionStyle: CSSProperties = {
    color: settings.description.color,
    fontFamily: settings.description.fontFamily,
    fontSize: settings.description.fontSize * textScale,
    fontWeight: settings.description.fontWeight,
    fontStyle: settings.description.fontStyle,
    textDecoration: settings.description.textDecoration,
    textTransform: settings.description.textTransform,
    letterSpacing: settings.description.letterSpacing,
    lineHeight: settings.description.lineHeight,
    textAlign: settings.description.textAlign,
  };
  const content = (
    <div
      ref={contentRef}
      className="qr-carousel__content"
      style={{
        gap: settings.textGap * textScale,
        alignItems:
          settings.contentAlign === "start"
            ? "flex-start"
            : settings.contentAlign === "end"
              ? "flex-end"
              : "center",
      }}
    >
      {title ? (
        <div ref={titleRef} className="qr-carousel__title" style={titleStyle}>
          {title}
        </div>
      ) : null}
      {description ? (
        <div ref={descriptionRef} className="qr-carousel__description" style={descriptionStyle}>
          {description}
        </div>
      ) : null}
    </div>
  );
  let renderedItem: ReactNode;
  if (kind === "imageCarousel") {
    renderedItem = imageSource ? (
      <img
        className="qr-carousel__image qr-carousel__image--only"
        src={imageSource}
        alt=""
        draggable={false}
        style={{ objectFit: settings.imageFit, objectPosition: settings.imagePosition }}
      />
    ) : null;
  } else if (kind === "textCarousel") {
    renderedItem = content;
  } else {
    renderedItem = (
      <div
        className={`qr-carousel__card-layout qr-carousel__card-layout--${settings.contentLayout}`}
      >
        {imageSource ? (
          <img
            className="qr-carousel__image"
            src={imageSource}
            alt=""
            draggable={false}
            style={{
              objectFit: settings.imageFit,
              objectPosition: settings.imagePosition,
              ...(settings.contentLayout === "left" || settings.contentLayout === "right"
                ? { width: `${settings.imageAreaPercent}%`, height: "100%" }
                : settings.contentLayout === "top" || settings.contentLayout === "bottom"
                  ? { width: "100%", height: `${settings.imageAreaPercent}%` }
                  : { width: "100%", height: "100%" }),
            }}
          />
        ) : null}
        {content}
      </div>
    );
  }

  return (
    <div
      className={`${className} qr-carousel`}
      style={
        {
          ...style,
          "--qr-carousel-duration": `${settings.transitionDurationMs}ms`,
          "--qr-carousel-exit-delay": `${Math.max(0, settings.intervalMs - settings.transitionDurationMs)}ms`,
          "--qr-carousel-indicator-color": settings.indicatorColor,
          "--qr-carousel-active-indicator-color": settings.activeIndicatorColor,
          "--qr-carousel-indicator-size": `${settings.indicatorSize}px`,
          "--qr-carousel-indicator-gap": `${settings.indicatorGap}px`,
          "--qr-carousel-arrow-color": settings.arrowColor,
          "--qr-carousel-arrow-background": settings.arrowBackgroundColor,
          "--qr-carousel-arrow-size": `${settings.arrowSize}px`,
        } as CSSProperties
      }
      data-transition={settings.transition}
      data-autoplay={settings.autoplay ? "true" : "false"}
      data-direction={settings.direction}
      data-indicator-position={settings.indicatorPosition}
      data-indicator-style={settings.indicatorStyle}
      aria-label={
        wordpress
          ? "WordPress posts"
          : kind === "imageCarousel"
            ? "Image carousel"
            : kind === "textCarousel"
              ? "Title and description carousel"
              : "Image, title, and description carousel"
      }
    >
      {items.length ? (
        <div
          key={`${activeIndex}-${items.length}`}
          className="qr-carousel__item"
          style={{
            backgroundColor: settings.cardBackgroundColor,
            borderColor: settings.cardBorderColor,
            borderWidth: settings.cardBorderWidth,
            borderRadius: settings.cardBorderRadius,
            padding: settings.cardPadding,
          }}
        >
          {renderedItem}
        </div>
      ) : (
        <div className="qr-carousel__empty">
          {wordpress
            ? "No published WordPress posts available"
            : kind === "imageCarousel"
              ? "Image list"
              : kind === "textCarousel"
                ? "Title and description list"
                : "Image, title, and description list"}
        </div>
      )}
      {settings.showArrows && items.length > 1 ? (
        <>
          {interactive ? (
            <>
              <button
                type="button"
                className={`qr-carousel__arrow qr-carousel__arrow--previous${vertical ? " qr-carousel__arrow--vertical" : ""}`}
                aria-label="Previous carousel item"
                disabled={!settings.loop && activeIndex === 0}
                onClick={() => goTo(activeIndex - 1)}
              >
                {previousSymbol}
              </button>
              <button
                type="button"
                className={`qr-carousel__arrow qr-carousel__arrow--next${vertical ? " qr-carousel__arrow--vertical" : ""}`}
                aria-label="Next carousel item"
                disabled={!settings.loop && activeIndex === items.length - 1}
                onClick={() => goTo(activeIndex + 1)}
              >
                {nextSymbol}
              </button>
            </>
          ) : (
            <>
              <span
                className={`qr-carousel__arrow qr-carousel__arrow--previous${vertical ? " qr-carousel__arrow--vertical" : ""}`}
                aria-hidden="true"
              >
                {previousSymbol}
              </span>
              <span
                className={`qr-carousel__arrow qr-carousel__arrow--next${vertical ? " qr-carousel__arrow--vertical" : ""}`}
                aria-hidden="true"
              >
                {nextSymbol}
              </span>
            </>
          )}
        </>
      ) : null}
      {settings.showIndicators && items.length > 1 ? (
        <div
          className="qr-carousel__indicators"
          aria-label={interactive ? "Carousel item selection" : undefined}
          aria-hidden={interactive ? undefined : "true"}
        >
          {items.map((_, index) =>
            interactive ? (
              <button
                type="button"
                key={index}
                className={index === activeIndex ? "is-active" : ""}
                aria-label={`Show carousel item ${index + 1}`}
                aria-pressed={index === activeIndex}
                onClick={() => goTo(index)}
              />
            ) : (
              <span key={index} className={index === activeIndex ? "is-active" : ""} />
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

function phoneDigits(value: unknown): string {
  return typeof value === "string" ? value.replace(/[^\d+]/g, "") : "";
}

function qrContent(type: LayoutNode["type"], value: unknown): string {
  if (type === "qrCall") {
    const phone = phoneDigits(value);
    return phone ? `tel:${phone}` : "";
  }
  if (type === "qrUrl") {
    const url = fieldText(value);
    return /^https?:\/\//i.test(url) ? url : "";
  }
  if (type === "qrWhatsApp") {
    if (!value || typeof value !== "object") return "";
    const phone = phoneDigits((value as { phone?: unknown }).phone).replace(/^\+/, "");
    const message =
      typeof (value as { message?: unknown }).message === "string"
        ? (value as { message: string }).message
        : "";
    return phone ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}` : "";
  }
  if (type === "qrVCard") return vCardQrContent(value);
  return fieldText(value);
}

function LiveTemporalNode({
  type,
  className,
  style,
  timezone,
  digitalClock,
  dateFormat,
  analogClock,
}: {
  type: "dateTime" | "digitalTime" | "displayDate" | "analogClock";
  className: string;
  style: CSSProperties;
  timezone?: string | undefined;
  digitalClock?: LayoutNode["digitalClock"];
  dateFormat?: string | undefined;
  analogClock?: LayoutNode["analogClock"];
}): ReactElement {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  if (type === "analogClock") {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hourCycle: "h23",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    }).formatToParts(now);
    const part = (name: Intl.DateTimeFormatPartTypes): number =>
      Number(parts.find((candidate) => candidate.type === name)?.value ?? 0);
    const seconds = part("second");
    const minutes = part("minute") + seconds / 60;
    const hours = (part("hour") % 12) + minutes / 60;
    const settings = {
      showHourMarkers: analogClock?.showHourMarkers ?? true,
      showSecondHand: analogClock?.showSecondHand ?? true,
      showNumbers: analogClock?.showNumbers ?? false,
      numberColor: analogClock?.numberColor ?? "#ffffff",
      numberFontFamily: analogClock?.numberFontFamily ?? "Inter, sans-serif",
      numberFontSize: analogClock?.numberFontSize ?? 24,
      numberFontWeight: analogClock?.numberFontWeight ?? 600,
      numberRadius: analogClock?.numberRadius ?? 36,
      markerColor: analogClock?.markerColor ?? "#ffffff",
      hourHandColor: analogClock?.hourHandColor ?? "#ffffff",
      minuteHandColor: analogClock?.minuteHandColor ?? "#ffffff",
      secondHandColor: analogClock?.secondHandColor ?? "#ef4444",
      markerLength: analogClock?.markerLength ?? 8,
      hourHandLength: analogClock?.hourHandLength ?? 25,
      minuteHandLength: analogClock?.minuteHandLength ?? 34,
      secondHandLength: analogClock?.secondHandLength ?? 38,
      handThickness: analogClock?.handThickness ?? 2.8,
    };
    return (
      <time
        className={`${className} qr-analog-clock`}
        style={style}
        dateTime={now.toISOString()}
        aria-label={new Intl.DateTimeFormat(undefined, {
          timeZone: timezone,
          timeStyle: "medium",
        }).format(now)}
      >
        {settings.showHourMarkers
          ? Array.from({ length: 12 }, (_, index) => (
              <i
                className="qr-analog-clock__marker"
                aria-hidden="true"
                key={index}
                style={{
                  backgroundColor: settings.markerColor,
                  height: `${settings.markerLength}%`,
                  transform: `translateX(-50%) rotate(${index * 30}deg)`,
                  transformOrigin: `50% ${(45 / settings.markerLength) * 100}%`,
                }}
              />
            ))
          : null}
        {settings.showNumbers
          ? Array.from({ length: 12 }, (_, index) => {
              const value = index + 1;
              const angle = (value * Math.PI) / 6 - Math.PI / 2;
              return (
                <span
                  className="qr-analog-clock__number"
                  aria-hidden="true"
                  key={value}
                  style={{
                    color: settings.numberColor,
                    fontFamily: settings.numberFontFamily,
                    fontSize: `${settings.numberFontSize}px`,
                    fontWeight: settings.numberFontWeight,
                    left: `${50 + Math.cos(angle) * settings.numberRadius}%`,
                    top: `${50 + Math.sin(angle) * settings.numberRadius}%`,
                  }}
                >
                  {value}
                </span>
              );
            })
          : null}
        <i
          className="qr-analog-clock__hand qr-analog-clock__hand--hour"
          aria-hidden="true"
          style={{
            backgroundColor: settings.hourHandColor,
            height: `${settings.hourHandLength}%`,
            width: `${settings.handThickness * 1.4}%`,
            transform: `translateX(-50%) rotate(${hours * 30}deg)`,
          }}
        />
        <i
          className="qr-analog-clock__hand qr-analog-clock__hand--minute"
          aria-hidden="true"
          style={{
            backgroundColor: settings.minuteHandColor,
            height: `${settings.minuteHandLength}%`,
            width: `${settings.handThickness}%`,
            transform: `translateX(-50%) rotate(${minutes * 6}deg)`,
          }}
        />
        {settings.showSecondHand ? (
          <i
            className="qr-analog-clock__hand qr-analog-clock__hand--second"
            aria-hidden="true"
            style={{
              backgroundColor: settings.secondHandColor,
              height: `${settings.secondHandLength}%`,
              width: `${Math.max(0.5, settings.handThickness * 0.55)}%`,
              transform: `translateX(-50%) rotate(${seconds * 6}deg)`,
            }}
          />
        ) : null}
        <i
          className="qr-analog-clock__pin"
          aria-hidden="true"
          style={{ backgroundColor: settings.hourHandColor }}
        />
      </time>
    );
  }
  const customDate = (): string => {
    const numericParts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(now);
    const part = (type: Intl.DateTimeFormatPartTypes): string =>
      numericParts.find((candidate) => candidate.type === type)?.value ?? "";
    const year = part("year");
    const month = part("month");
    const day = part("day");
    const monthLong = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      month: "long",
    }).format(now);
    const monthShort = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      month: "short",
    }).format(now);
    const weekdayLong = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      weekday: "long",
    }).format(now);
    const weekdayShort = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      weekday: "short",
    }).format(now);
    const tokens: Record<string, string> = {
      YYYY: year,
      YY: year.slice(-2),
      MMMM: monthLong,
      MMM: monthShort,
      MM: month,
      M: String(Number(month)),
      DD: day,
      D: String(Number(day)),
      dddd: weekdayLong,
      ddd: weekdayShort,
    };
    return (dateFormat?.trim() || "dddd, MMMM D, YYYY").replace(
      /YYYY|MMMM|dddd|MMM|ddd|YY|MM|DD|M|D/g,
      (token) => tokens[token] ?? token,
    );
  };
  const formatted =
    type === "digitalTime"
      ? new Intl.DateTimeFormat(undefined, {
          timeZone: timezone,
          hour12: !(digitalClock?.use24Hour ?? false),
          hour: "2-digit",
          minute: "2-digit",
          ...((digitalClock?.showSeconds ?? true) ? { second: "2-digit" as const } : {}),
        }).format(now)
      : type === "displayDate"
        ? customDate()
        : new Intl.DateTimeFormat(undefined, {
            timeZone: timezone,
            dateStyle: "full",
            timeStyle: "short",
          }).format(now);
  return (
    <time className={className} style={style} dateTime={now.toISOString()}>
      {formatted}
    </time>
  );
}

interface VideoPlaybackOptions {
  waitForEnd: boolean;
  loop: boolean;
  muted: boolean;
  onStateChange?: ((videoId: string, state: VideoPlaybackState) => void) | undefined;
}

export interface MediaControlCommand {
  id: string;
  targetId: string;
  action: "playPause" | "next" | "previous" | "mute";
}

function YouTubeFrame({
  id,
  playlistId,
  playlistVideoIds,
  shuffle = false,
  skipFirst = false,
  forceLoop = false,
  playRecommended = false,
  videoId,
  className,
  style,
  playback,
  controlCommand,
  controlTargetId,
}: {
  id: string;
  playlistId?: string | undefined;
  playlistVideoIds?: string[] | undefined;
  shuffle?: boolean | undefined;
  skipFirst?: boolean | undefined;
  forceLoop?: boolean | undefined;
  playRecommended?: boolean | undefined;
  videoId: string;
  className: string;
  style: CSSProperties;
  playback: VideoPlaybackOptions;
  controlCommand?: MediaControlCommand | null | undefined;
  controlTargetId: string;
}): ReactElement {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const ended = useRef(false);
  const playerState = useRef(-1);
  const muted = useRef(playback.muted);
  const handledCommand = useRef("");
  const shuffleAdvanced = useRef(false);

  useEffect(() => {
    if (
      !controlCommand ||
      controlCommand.targetId !== controlTargetId ||
      handledCommand.current === controlCommand.id
    )
      return;
    handledCommand.current = controlCommand.id;
    const target = frameRef.current?.contentWindow;
    if (!target) return;
    const func =
      controlCommand.action === "playPause"
        ? playerState.current === 1
          ? "pauseVideo"
          : "playVideo"
        : controlCommand.action === "next"
          ? "nextVideo"
          : controlCommand.action === "previous"
            ? "previousVideo"
            : muted.current
              ? "unMute"
              : "mute";
    if (controlCommand.action === "mute") muted.current = !muted.current;
    target.postMessage(JSON.stringify({ event: "command", func, args: [] }), "*");
  }, [controlCommand, controlTargetId]);

  useEffect(() => {
    ended.current = false;
    shuffleAdvanced.current = false;
    muted.current = playback.muted;
  }, [id, playback.muted]);

  useEffect(() => {
    if (playback.muted) return;
    const enableSound = (): void => {
      enableYoutubeSound(frameRef.current?.contentWindow);
      muted.current = false;
    };
    window.addEventListener(playerMediaInteractionEvent, enableSound);
    return () => window.removeEventListener(playerMediaInteractionEvent, enableSound);
  }, [id, playback.muted]);

  useEffect(() => {
    const onMessage = (event: MessageEvent<unknown>): void => {
      if (
        event.origin !== "https://www.youtube-nocookie.com" ||
        event.source !== frameRef.current?.contentWindow
      )
        return;
      let message: unknown = event.data;
      if (typeof message === "string") {
        try {
          message = JSON.parse(message);
        } catch {
          return;
        }
      }
      if (!message || typeof message !== "object") return;
      const payload = message as {
        event?: unknown;
        info?: unknown;
      };
      if (payload.event === "onReady") {
        const target = frameRef.current?.contentWindow;
        if (shuffle && !shuffleAdvanced.current) {
          shuffleAdvanced.current = true;
          startYoutubeShuffle(target, skipFirst ? youtubePlaylistStartIndex(true) : undefined);
        }
      }
      const state =
        payload.event === "onStateChange"
          ? payload.info
          : payload.event === "infoDelivery" &&
              payload.info &&
              typeof payload.info === "object" &&
              "playerState" in payload.info
            ? (payload.info as { playerState?: unknown }).playerState
            : undefined;
      if (typeof state === "number") playerState.current = state;
      if (payload.event === "infoDelivery" && payload.info && typeof payload.info === "object") {
        const info = payload.info as { muted?: unknown };
        if (typeof info.muted === "boolean") muted.current = info.muted;
      }
      if (state === 1) {
        if (playback.waitForEnd) playback.onStateChange?.(videoId, "playing");
      }
      if (state === 0 && !ended.current && playback.waitForEnd) {
        ended.current = true;
        playback.onStateChange?.(videoId, "ended");
      }
      if (state === 0 && playRecommended && !playback.waitForEnd)
        frameRef.current?.contentWindow?.postMessage(
          JSON.stringify({ event: "command", func: "nextVideo", args: [] }),
          "*",
        );
      if (payload.event === "onError" && playback.waitForEnd)
        playback.onStateChange?.(videoId, "error");
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [playback, playRecommended, shuffle, videoId]);

  const listen = (): void => {
    const target = frameRef.current?.contentWindow;
    target?.postMessage(JSON.stringify({ event: "listening", id: videoId }), "*");
    target?.postMessage(
      JSON.stringify({
        event: "command",
        func: "addEventListener",
        args: ["onStateChange"],
      }),
      "*",
    );
    target?.postMessage(
      JSON.stringify({ event: "command", func: "addEventListener", args: ["onReady"] }),
      "*",
    );
  };

  const playbackParams = [
    "autoplay=1",
    `mute=${playback.muted ? 1 : 0}`,
    (forceLoop || playback.loop) && !playback.waitForEnd ? "loop=1" : "",
    playlistId ? `list=${playlistId}` : "",
    playlistId && skipFirst ? `index=${youtubePlaylistStartIndex(true)}` : "",
    playlistVideoIds?.length ? `playlist=${playlistVideoIds.join(",")}` : "",
    !playlistId && !playlistVideoIds?.length && playback.loop && !playback.waitForEnd
      ? `playlist=${id}`
      : "",
    "controls=0",
    "disablekb=1",
    "fs=0",
    "iv_load_policy=3",
    "playsinline=1",
    `rel=${playRecommended ? 1 : 0}`,
    "enablejsapi=1",
    typeof window === "undefined" ? "" : `origin=${encodeURIComponent(window.location.origin)}`,
  ]
    .filter(Boolean)
    .join("&");
  return (
    <iframe
      ref={frameRef}
      className={className}
      style={style}
      src={`https://www.youtube-nocookie.com/embed/${playlistId ? "videoseries" : id}?${playbackParams}`}
      title="Autoplaying YouTube video"
      allow="autoplay; encrypted-media"
      referrerPolicy="strict-origin-when-cross-origin"
      tabIndex={-1}
      aria-hidden="true"
      onLoad={listen}
    />
  );
}

interface NodeProps {
  node: LayoutNode;
  values: SlideFieldValues;
  orientation: "landscape" | "portrait";
  path: string;
  videoPlayback: VideoPlaybackOptions;
  timezone?: string | undefined;
  controlCommand?: MediaControlCommand | null | undefined;
  controlNamespace: string;
  interactive: boolean;
}

function RenderNode({
  node,
  values,
  orientation,
  path,
  videoPlayback,
  timezone,
  controlCommand,
  controlNamespace,
  interactive,
}: NodeProps): ReactElement | null {
  const wordpressPlayback = useContext(WordPressPlaybackContext);
  if (node.visibleWhen && values[node.visibleWhen.field] !== node.visibleWhen.equals) return null;
  const responsiveStyles = node.responsive?.[orientation];
  const style = {
    ...toCssProperties(node.styles),
    ...toCssProperties(responsiveStyles),
    ...centerPositionStyles(node),
    ...(node.animationDurationMs
      ? { "--qr-animation-duration": `${node.animationDurationMs}ms` }
      : {}),
    ...(node.animationDelayMs ? { "--qr-animation-delay": `${node.animationDelayMs}ms` } : {}),
    ...(node.animationIteration === "infinite" ? { "--qr-animation-iteration": "infinite" } : {}),
  };
  const value = node.binding ? values[node.binding] : undefined;
  const children = node.children?.map((child, index) => (
    <RenderNode
      key={child.id ?? `${child.type}-${child.binding ?? "unbound"}-${index}`}
      node={child}
      values={values}
      orientation={orientation}
      path={`${path}.${index}`}
      videoPlayback={videoPlayback}
      timezone={timezone}
      controlCommand={controlCommand}
      controlNamespace={controlNamespace}
      interactive={interactive}
    />
  ));
  const className = [
    `qr-node qr-node--${node.type}`,
    node.variant ? `qr-text--${node.variant}` : "",
    node.animation && node.animation !== "none" ? `qr-animation--${node.animation}` : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (node.spacing) {
    const resolvedSpacing = {
      ...widgetSpacingFromStyles({ ...node.styles, ...responsiveStyles }),
      ...node.spacing,
    };
    const spacingStyles = widgetSpacingStyles(resolvedSpacing);
    const outerStyle: CSSProperties = {
      ...style,
      padding: 0,
      boxSizing: "border-box",
      overflow: "hidden",
    };
    for (const key of widgetSpacingKeys) {
      delete outerStyle[key];
      if (key.startsWith("margin")) outerStyle[key] = spacingStyles[key];
    }
    const innerStyles = { ...node.styles, ...responsiveStyles };
    for (const key of widgetSpacingKeys) delete innerStyles[key];
    Object.assign(innerStyles, {
      position: "relative",
      left: 0,
      top: 0,
      right: "auto",
      bottom: "auto",
      width: "100%",
      height: "100%",
      minWidth: 0,
      minHeight: 0,
      maxWidth: "none",
      maxHeight: "none",
      margin: 0,
      padding: 0,
      border: 0,
      borderWidth: 0,
      borderRadius: 0,
      background: "transparent",
      backgroundColor: "transparent",
      opacity: 1,
      transform: "none",
    });
    const innerNode: LayoutNode = {
      ...node,
      spacing: undefined,
      styles: innerStyles,
      responsive: undefined,
      positioning: undefined,
      animation: undefined,
      animationDurationMs: undefined,
      animationDelayMs: undefined,
      animationIteration: undefined,
    };
    return (
      <div
        className={`qr-node qr-widget-box${node.animation && node.animation !== "none" ? ` qr-animation--${node.animation}` : ""}`}
        style={outerStyle}
      >
        <div
          className="qr-widget-padding"
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            ...Object.fromEntries(
              Object.entries(spacingStyles).filter(([key]) => key.startsWith("padding")),
            ),
          }}
        >
          <RenderNode
            node={innerNode}
            values={values}
            orientation={orientation}
            path={path}
            videoPlayback={videoPlayback}
            timezone={timezone}
            controlCommand={controlCommand}
            controlNamespace={controlNamespace}
            interactive={interactive}
          />
        </div>
      </div>
    );
  }

  switch (node.type) {
    case "container":
    case "stack":
    case "grid":
    case "overlay":
    case "shape":
    case "square":
    case "circle":
    case "line":
    case "spacer":
      return (
        <div className={className} style={style}>
          {children}
        </div>
      );
    case "verticalLabel":
      return (
        <div className={className} style={style}>
          <TextBox
            key={JSON.stringify([node.verticalLabelOrientation, style, value])}
            className="qr-vertical-label__text"
            node={node}
            text={fieldText(value)}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              boxSizing: "border-box",
              padding: style.padding,
              overflow: "hidden",
              overflowWrap: "anywhere",
              writingMode: "vertical-rl",
              WebkitWritingMode: "vertical-rl",
              textOrientation: node.verticalLabelOrientation === "upright" ? "upright" : "mixed",
              transform:
                node.verticalLabelOrientation === "counterclockwise" ? "rotate(180deg)" : undefined,
            }}
          />
        </div>
      );
    case "text":
    case "label":
      return <TextBox className={className} style={style} node={node} text={fieldText(value)} />;
    case "ticker": {
      const items = tickerItems(value);
      return items.length ? (
        <Ticker className={className} style={style} node={node} items={items} />
      ) : (
        <div className={`${className} qr-media-placeholder`} style={style}>
          <span>News ticker</span>
        </div>
      );
    }
    case "html":
      return (
        <iframe
          className={className}
          style={style}
          srcDoc={fieldHtml(value)}
          sandbox=""
          title="Slide HTML content"
          tabIndex={-1}
          aria-hidden="true"
        />
      );
    case "staticImage":
    case "image":
    case "logo": {
      const src =
        node.type === "staticImage"
          ? node.staticImageAssetId
            ? `/api/v1/media/assets/${node.staticImageAssetId}`
            : undefined
          : assetUrl(value);
      return src ? (
        <img className={className} style={style} src={src} alt="" draggable={false} />
      ) : (
        <div className={`${className} qr-media-placeholder`} style={style}>
          <span>{node.type === "logo" ? "Logo" : "Image"}</span>
        </div>
      );
    }
    case "imageCarousel":
    case "textCarousel":
    case "contentCarousel":
    case "wordpressPosts":
      return (
        <Carousel
          kind={node.type === "wordpressPosts" ? "textCarousel" : node.type}
          wordpress={node.type === "wordpressPosts"}
          className={className}
          style={style}
          node={node}
          value={
            node.type === "wordpressPosts"
              ? value && typeof value === "object" && "posts" in value
                ? value.posts
                : []
              : value
          }
          interactive={interactive}
        />
      );
    case "video": {
      const src = assetUrl(value);
      return src ? (
        <video
          className={className}
          style={style}
          src={src}
          autoPlay
          muted={videoPlayback.muted}
          controls={false}
          controlsList="nodownload nofullscreen noremoteplayback"
          disablePictureInPicture
          loop={videoPlayback.loop && !videoPlayback.waitForEnd}
          playsInline
          tabIndex={-1}
          aria-hidden="true"
          draggable={false}
          onPlay={() => videoPlayback.onStateChange?.(path, "playing")}
          onEnded={() => videoPlayback.onStateChange?.(path, "ended")}
          onError={() => videoPlayback.onStateChange?.(path, "error")}
        />
      ) : (
        <div className={`${className} qr-media-placeholder`} style={style}>
          <span>Video</span>
        </div>
      );
    }
    case "youtube": {
      const parsed = youtubeValueSchema.safeParse(value);
      const playbackMode =
        parsed.success && typeof parsed.data !== "string" ? parsed.data.playbackMode : undefined;
      const shuffle =
        parsed.success && typeof parsed.data !== "string" ? parsed.data.shuffle : false;
      const playlistId = youtubePlaylistId(value);
      const id = playlistId ? "videoseries" : youtubeVideoId(value);
      return id ? (
        <YouTubeFrame
          id={id}
          playlistId={playlistId}
          shuffle={Boolean(playlistId) && shuffle}
          skipFirst={Boolean(playlistId) && shuffle}
          forceLoop={Boolean(playlistId) || playbackMode === "loop"}
          playRecommended={playbackMode === "recommended"}
          videoId={path}
          className={className}
          style={style}
          playback={
            playbackMode === "once" || playbackMode === "recommended"
              ? { ...videoPlayback, loop: false }
              : videoPlayback
          }
          controlCommand={controlCommand}
          controlTargetId={`${controlNamespace}:${path}`}
        />
      ) : (
        <div className={`${className} qr-media-placeholder`} style={style}>
          <span>YouTube</span>
        </div>
      );
    }
    case "youtubeList": {
      const parsed = youtubeListValueSchema.safeParse(value);
      const ids = parsed.success
        ? parsed.data.urls
            .map((url) => youtubeVideoId(url))
            .filter((id): id is string => Boolean(id))
        : [];
      const shuffle = parsed.success ? parsed.data.shuffle : false;
      const orderedIds = rotateYoutubeListForInitialSkip(ids, shuffle);
      return ids.length ? (
        <YouTubeFrame
          id={orderedIds[0]!}
          playlistVideoIds={orderedIds.length > 1 ? orderedIds.slice(1) : orderedIds}
          shuffle={shuffle}
          forceLoop
          videoId={path}
          className={className}
          style={style}
          playback={videoPlayback}
          controlCommand={controlCommand}
          controlTargetId={`${controlNamespace}:${path}`}
        />
      ) : (
        <div className={`${className} qr-media-placeholder`} style={style}>
          <span>YouTube List</span>
        </div>
      );
    }
    case "dateTime":
    case "digitalTime":
    case "displayDate":
    case "analogClock":
      return (
        <LiveTemporalNode
          type={node.type}
          className={className}
          style={style}
          timezone={timezone}
          digitalClock={node.digitalClock}
          dateFormat={node.dateFormat}
          analogClock={node.analogClock}
        />
      );
    case "wordpressPostQr":
    case "qrCode":
    case "qrCall":
    case "qrUrl":
    case "qrWhatsApp":
    case "qrVCard": {
      const qrValue =
        node.type === "wordpressPostQr"
          ? wordpressPostUrl(
              values[node.wordpressSourceBinding ?? ""],
              wordpressPlayback?.indices.get(node.wordpressSourceBinding ?? "") ?? 0,
            )
          : qrContent(node.type, value);
      if (node.type === "wordpressPostQr" && !qrValue) return null;
      const foreground =
        typeof style.color === "string" && /^#[0-9a-f]{6}$/i.test(style.color)
          ? style.color
          : "#111111";
      const background =
        typeof style.backgroundColor === "string" && /^#[0-9a-f]{6}$/i.test(style.backgroundColor)
          ? style.backgroundColor
          : "#ffffff";
      return qrValue ? (
        <div
          className={className}
          style={style}
          role={node.type === "wordpressPostQr" ? "img" : undefined}
          aria-label={
            node.type === "wordpressPostQr" ? "QR code for current WordPress post" : undefined
          }
        >
          <div className="qr-code-surface" style={{ backgroundColor: background }}>
            <QRCodeSVG
              className="qr-code-vector"
              value={qrValue}
              width="100%"
              height="100%"
              level="M"
              marginSize={node.type === "wordpressPostQr" ? 4 : 2}
              fgColor={foreground}
              bgColor={background}
              aria-hidden="true"
            />
            <QRCodeCanvas
              className="qr-code-bitmap"
              value={qrValue}
              size={1024}
              level="M"
              marginSize={node.type === "wordpressPostQr" ? 4 : 2}
              fgColor={foreground}
              bgColor="rgba(0,0,0,0)"
              aria-label="QR code"
            />
          </div>
        </div>
      ) : (
        <div className={`${className} qr-media-placeholder`} style={style}>
          <span>QR</span>
        </div>
      );
    }
  }
}

export interface SlideRendererProps {
  definition: SlideTypeDefinition;
  values: SlideFieldValues;
  orientation?: "landscape" | "portrait";
  className?: string;
  ariaLabel?: string;
  waitForVideoEnd?: boolean;
  videoLoop?: boolean;
  videoMuted?: boolean;
  onVideoPlaybackStateChange?: ((videoId: string, state: VideoPlaybackState) => void) | undefined;
  timezone?: string | undefined;
  mediaControlCommand?: MediaControlCommand | null | undefined;
  mediaControlNamespace?: string | undefined;
  interactive?: boolean;
}

export function SlideRenderer({
  definition,
  values,
  orientation = "landscape",
  className = "",
  ariaLabel,
  waitForVideoEnd = false,
  videoLoop = true,
  videoMuted = true,
  onVideoPlaybackStateChange,
  timezone,
  mediaControlCommand,
  mediaControlNamespace = "slide",
  interactive = true,
}: SlideRendererProps): ReactElement {
  const [wordpressIndices, setWordpressIndices] = useState<Map<string, number>>(() => new Map());
  const wordpressPlayback = useMemo(
    () => ({ indices: wordpressIndices, setIndices: setWordpressIndices }),
    [wordpressIndices],
  );
  const aspectRatio = `${definition.canvas.width} / ${definition.canvas.height}`;
  const canvasStyle = useMemo<CSSProperties>(
    () => ({
      aspectRatio,
      backgroundColor: definition.canvas.backgroundColor,
      ...toCssProperties(definition.defaultStyles),
    }),
    [aspectRatio, definition.canvas.backgroundColor, definition.defaultStyles],
  );
  const videoPlayback = useMemo<VideoPlaybackOptions>(
    () => ({
      waitForEnd: waitForVideoEnd,
      loop: videoLoop,
      muted: videoMuted,
      onStateChange: onVideoPlaybackStateChange,
    }),
    [onVideoPlaybackStateChange, videoLoop, videoMuted, waitForVideoEnd],
  );
  const renderedValues = useMemo<SlideFieldValues>(() => {
    const resolved = { ...values };
    for (const field of definition.fields) {
      if ((field.adminOnly || field.type === "html") && field.defaultValue !== undefined)
        resolved[field.key] = field.defaultValue;
    }
    return resolved;
  }, [definition.fields, values]);

  return (
    <WordPressPlaybackContext.Provider value={wordpressPlayback}>
      <section
        className={`qr-slide-canvas ${className}`}
        style={canvasStyle}
        aria-label={ariaLabel ?? definition.name}
      >
        <RenderNode
          node={definition.layout}
          values={renderedValues}
          orientation={orientation}
          path="root"
          videoPlayback={videoPlayback}
          timezone={timezone}
          controlCommand={mediaControlCommand}
          controlNamespace={mediaControlNamespace}
          interactive={interactive}
        />
      </section>
    </WordPressPlaybackContext.Provider>
  );
}
